import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import {
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  TablePagination,
} from '@mui/material'
import { SectionHeader } from '../../features/section-components/SectionHeader'
import MarginWrapper from '../../features/wrappers/MarginWrapper'
import styles from './Security.module.scss'
import {
  selectLoginHistoryObject,
  getLoginHistoryError,
  getLoginHistoryStatus,
  fetchLoginHistory
} from '../../features/user/LoginHistorySlice'
import {
  formatLastLogin,
  formatApplication,
  formatDeviceSystem,
  formatLocation
} from './SecurityFormatters'
import JNJFooter from '../../features/jnj-footer/JNJFooter'

const PAGE_SIZE = 20

function SecurityDesktop({ t }) {
  const dispatch = useDispatch()
  const loginHistoryObj = useSelector(selectLoginHistoryObject)
  const status = useSelector(getLoginHistoryStatus)
  const error = useSelector(getLoginHistoryError)

  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE)

  useEffect(() => {
    dispatch(fetchLoginHistory({ pageSize: rowsPerPage, pageOffset: page }))
  }, [dispatch, page, rowsPerPage])

  const rows = loginHistoryObj?.history || []
  const total = loginHistoryObj?.total || 0

  const handleChangePage = (_event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value)
    setPage(0)
  }

  return (
    <MarginWrapper>
      <section className={styles.securityContainer}>
        <header className={styles.headerBlock}>
          <SectionHeader text={t('security-page-header')} size="large" />
          <h4 className={styles.subtitle}>{t('security-page-subtitle')}</h4>
        </header>

        <section className={styles.tableBlock} aria-live="polite">
          {status === 'loading' && (
            <div className={styles.stateMsg}>
              <div className={styles.loadingBox}>
                <CircularProgress size={36} thickness={4} />
              </div>
            </div>
          )}

          {status === 'failed' && <div className={styles.stateMsg}>{error || t('generic-error')}</div>}

          {status === 'succeeded' && (
            <TableContainer className={styles.tableWrap}>
              <Table className={styles.loginHistoryTable}>
                <TableHead>
                  <TableRow>
                    <TableCell className={styles.colLastLogin}>{t('security-login-history-lastLogin')}</TableCell>
                    <TableCell className={styles.colApplication}>{t('security-login-history-application')}</TableCell>
                    <TableCell className={styles.colDeviceSystem}>{t('security-login-history-deviceSystem')}</TableCell>
                    <TableCell className={styles.colLocation}>{t('security-login-history-location')}</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.map((item) => (
                    <TableRow key={`${item?.published || 'row'}-${item.published}`}>
                      <TableCell>{formatLastLogin(item?.published)}</TableCell>
                      <TableCell>{formatApplication(item?.eventType)}</TableCell>
                      <TableCell>{formatDeviceSystem(item)}</TableCell>
                      <TableCell>{formatLocation(item)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <TablePagination
                component="div"
                className={styles.tablePagination}
                disabled
                count={total}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[20, 20]}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={t('logs-per-page')}
                labelDisplayedRows={({ from, count }) =>
                  `${t('logs-displaying')} ${from} ${t('logs-per-page-of')} ${count}`}
                sx={{ color: '#564C47', marginTop: '44px' }}
              />
            </TableContainer>
          )}
        </section>
      </section>

      <div className={styles.jnjFooter}>
        <JNJFooter text={t('FooterText')} footerSize="medium" />
      </div>
    </MarginWrapper>
  )
}

SecurityDesktop.propTypes = {
  t: PropTypes.func.isRequired
}

export default SecurityDesktop
